import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Collapse,
  IconButton,
  Tooltip,
  Badge,
  alpha,
  useTheme,
  Paper,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Work as WorkIcon,
  Security as SecurityIcon,
  HealthAndSafety as HealthIcon,
  DirectionsCar as CarIcon,
  AccountBalance as AccountBalanceIcon,
  Folder as FolderIcon,
  Build as BuildIcon,
  AutoAwesome as AutoAwesomeIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import {
  CATEGORIES,
  AREAS,
  DOCUMENT_TYPES as documentTypes,
  getTypesByCategory,
  getTypesByArea,
  getPopularTypes,
  searchDocumentTypes,
  DocumentType, DocumentCategory, DocumentArea
} from './documentTypes';

interface DocumentTypeSelectorProps {
  selectedType: DocumentType | null;
  onTypeSelect: (type: DocumentType) => void;
  onNeedHelp?: () => void;
}

// Ícones por área
const AREA_ICONS: Record<string, React.ReactNode> = {
  civel: <GavelIcon sx={{ fontSize: 18 }} />,
  trabalhista: <WorkIcon sx={{ fontSize: 18 }} />,
  criminal: <SecurityIcon sx={{ fontSize: 18 }} />,
  previdenciario: <HealthIcon sx={{ fontSize: 18 }} />,
  transito: <CarIcon sx={{ fontSize: 18 }} />,
  magistratura: <AccountBalanceIcon sx={{ fontSize: 18 }} />,
  recesso: <FolderIcon sx={{ fontSize: 18 }} />,
  ambiental: <FolderIcon sx={{ fontSize: 18 }} />,
  familia: <FolderIcon sx={{ fontSize: 18 }} />,
  geral: <DescriptionIcon sx={{ fontSize: 18 }} />,
};

// Ícones por categoria
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  pecas_juridicas: <DescriptionIcon />,
  contratos: <DescriptionIcon />,
  extrajudicial: <FolderIcon />,
  ferramentas: <BuildIcon />,
};

const DocumentTypeSelector: React.FC<DocumentTypeSelectorProps> = ({
  selectedType,
  onTypeSelect,
  onNeedHelp,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory>('pecas_juridicas');
  const [selectedAreaFilter, setSelectedAreaFilter] = useState<DocumentArea | null>(null);
  const [showAllPopular, setShowAllPopular] = useState(false);

  // Tipos populares
  const popularTypes = useMemo(() => getPopularTypes(), []);

  // Filtrar tipos baseado na busca
  const filteredTypes = useMemo(() => {
    if (searchQuery.trim()) {
      return searchDocumentTypes(searchQuery);
    }
    
    let types = getTypesByCategory(selectedCategory);
    
    if (selectedAreaFilter) {
      types = types.filter(t => t.area === selectedAreaFilter);
    }
    
    return types;
  }, [searchQuery, selectedCategory, selectedAreaFilter]);

  // Agrupar tipos por área
  const typesByArea = useMemo(() => {
    const grouped: Record<string, DocumentType[]> = {};
    filteredTypes.forEach(type => {
      if (!grouped[type.area]) {
        grouped[type.area] = [];
      }
      grouped[type.area].push(type);
    });
    return grouped;
  }, [filteredTypes]);

  // Áreas disponíveis na categoria selecionada
  const availableAreas = useMemo(() => {
    const categoryTypes = getTypesByCategory(selectedCategory);
    const areaIds = Array.from(new Set(categoryTypes.map(t => t.area)));
    return AREAS.filter(a => areaIds.includes(a.id));
  }, [selectedCategory]);

  const getAreaConfig = (areaId: string) => {
    return AREAS.find(a => a.id === areaId);
  };

  const handleCategoryChange = (_: React.SyntheticEvent, newValue: DocumentCategory) => {
    setSelectedCategory(newValue);
    setSelectedAreaFilter(null);
    setSearchQuery('');
  };

  const renderTypeCard = (type: DocumentType, compact = false) => {
    const isSelected = selectedType?.id === type.id;
    const areaConfig = getAreaConfig(type.area);
    
    return (
      <Card
        key={type.id}
        elevation={isSelected ? 4 : 1}
        sx={{
          border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'grey.200',
          bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-2px)',
            boxShadow: 3,
          },
        }}
      >
        <CardActionArea onClick={() => onTypeSelect(type)}>
          <CardContent sx={{ p: compact ? 1.5 : 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: compact ? 32 : 40,
                  height: compact ? 32 : 40,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(areaConfig?.color || '#4a5568', 0.1),
                  color: areaConfig?.color || 'text.secondary',
                  flexShrink: 0,
                }}
              >
                {AREA_ICONS[type.area] || <DescriptionIcon sx={{ fontSize: compact ? 16 : 20 }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {compact ? (
                  <>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{ color: 'text.primary' }}
                    >
                      {type.name}
                    </Typography>
                    <Chip
                      label={areaConfig?.name?.substring(0, 5) || type.area}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        mt: 0.5,
                        bgcolor: alpha(areaConfig?.color || '#4a5568', 0.1),
                        color: areaConfig?.color || 'text.secondary',
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ color: 'text.primary', mb: 0.5 }}
                    >
                      {type.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                      }}
                    >
                      {type.description}
                    </Typography>
                    <Chip
                      label={areaConfig?.name || type.area}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        mt: 1,
                        bgcolor: alpha(areaConfig?.color || '#4a5568', 0.1),
                        color: areaConfig?.color || 'text.secondary',
                      }}
                    />
                  </>
                )}
              </Box>
              {isSelected && (
                <CheckCircleIcon 
                  sx={{ 
                    color: 'primary.main', 
                    fontSize: compact ? 18 : 22,
                    flexShrink: 0,
                  }} 
                />
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Box>
      {/* Cabeçalho com botão de ajuda */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
            Qual documento você deseja criar?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Selecione o tipo de documento para prosseguir com a criação
          </Typography>
        </Box>
        
        {/* Botão de Ajuda IA */}
        {onNeedHelp && (
          <Paper
            elevation={0}
            onClick={onNeedHelp}
            sx={{
              p: 2,
              maxWidth: 300,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'primary.light',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              flexShrink: 0,
              '&:hover': {
                borderColor: 'primary.main',
                borderStyle: 'solid',
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                transform: 'translateY(-1px)',
                boxShadow: 1
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <AutoAwesomeIcon 
                sx={{ 
                  color: 'primary.main', 
                  fontSize: 22,
                  mt: 0.25
                }} 
              />
              <Box>
                <Typography 
                  variant="body2" 
                  fontWeight={600} 
                  color="primary.main"
                  sx={{ mb: 0.5 }}
                >
                  Não sabe qual documento criar?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                  Compartilhe o contexto, documentos ou áudio para a IA te ajudar
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Busca */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar tipo de documento..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Seção de Mais Utilizados */}
      {!searchQuery && (
        <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ color: 'warning.main', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Mais utilizados
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setShowAllPopular(!showAllPopular)}
            >
              {showAllPopular ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Grid container spacing={1.5}>
            {(showAllPopular ? popularTypes : popularTypes.slice(0, 8)).map((type) => (
              <Grid item xs={6} sm={4} md={3} key={type.id}>
                {renderTypeCard(type, true)}
              </Grid>
            ))}
          </Grid>

          {!showAllPopular && popularTypes.length > 8 && (
            <Typography
              variant="body2"
              color="primary"
              sx={{
                mt: 2,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => setShowAllPopular(true)}
            >
              Ver todos os {popularTypes.length} mais utilizados
            </Typography>
          )}
        </Paper>
      )}

      {/* Tabs de Categorias */}
      {!searchQuery && (
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
              },
            }}
          >
            {CATEGORIES.map((category) => (
              <Tab
                key={category.id}
                value={category.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {CATEGORY_ICONS[category.id]}
                    <span>{category.name}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>

          {/* Filtros por Área */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Todos"
              variant={selectedAreaFilter === null ? 'filled' : 'outlined'}
              onClick={() => setSelectedAreaFilter(null)}
              sx={{
                fontWeight: selectedAreaFilter === null ? 600 : 400,
                bgcolor: selectedAreaFilter === null ? 'primary.main' : 'transparent',
                color: selectedAreaFilter === null ? 'white' : 'text.primary',
                '&:hover': {
                  bgcolor: selectedAreaFilter === null ? 'primary.dark' : alpha(theme.palette.primary.main, 0.1),
                },
              }}
            />
            {availableAreas.map((area) => (
              <Chip
                key={area.id}
                label={area.name}
                variant={selectedAreaFilter === area.id ? 'filled' : 'outlined'}
                onClick={() => setSelectedAreaFilter(area.id)}
                sx={{
                  fontWeight: selectedAreaFilter === area.id ? 600 : 400,
                  bgcolor: selectedAreaFilter === area.id ? area.color : 'transparent',
                  color: selectedAreaFilter === area.id ? 'white' : 'text.primary',
                  borderColor: area.color,
                  '&:hover': {
                    bgcolor: selectedAreaFilter === area.id ? area.color : alpha(area.color, 0.1),
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Lista de Tipos por Área */}
      <Box>
        {searchQuery ? (
          // Resultados da busca
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredTypes.length} resultado(s) encontrado(s)
            </Typography>
            <Grid container spacing={2}>
              {filteredTypes.map((type) => (
                <Grid item xs={12} sm={6} md={4} key={type.id}>
                  {renderTypeCard(type)}
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          // Tipos agrupados por área
          Object.entries(typesByArea).map(([areaId, types]) => {
            const areaConfig = getAreaConfig(areaId);
            // Se a área está filtrada, mostrar todos os tipos; caso contrário, limitar a 6
            const isAreaFiltered = selectedAreaFilter === areaId;
            const displayTypes = isAreaFiltered ? types : types.slice(0, 6);
            const hasMore = !isAreaFiltered && types.length > 6;

            return (
              <Paper key={areaId} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(areaConfig?.color || '#4a5568', 0.1),
                      color: areaConfig?.color || 'text.secondary',
                    }}
                  >
                    {AREA_ICONS[areaId]}
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {areaConfig?.name || areaId}
                  </Typography>
                  <Chip
                    label={types.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'grey.100',
                    }}
                  />
                  {isAreaFiltered && (
                    <Typography 
                      variant="caption" 
                      color="primary"
                      sx={{ 
                        cursor: 'pointer',
                        ml: 'auto',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => setSelectedAreaFilter(null)}
                    >
                      Recolher
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={2}>
                  {displayTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={4} key={type.id}>
                      {renderTypeCard(type)}
                    </Grid>
                  ))}
                </Grid>

                {hasMore && (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{
                      mt: 2,
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => setSelectedAreaFilter(areaId as DocumentArea)}
                  >
                    Ver todos os {types.length} tipos de {areaConfig?.name}
                  </Typography>
                )}
              </Paper>
            );
          })
        )}
      </Box>

      {/* Tipo Selecionado */}
      {selectedType && (
        <Paper
          elevation={2}
          sx={{
            mt: 3,
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Tipo selecionado:
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedType.name}
            </Typography>
          </Box>
          <Chip
            label={getAreaConfig(selectedType.area)?.name || selectedType.area}
            size="small"
            sx={{
              bgcolor: alpha(getAreaConfig(selectedType.area)?.color || '#6b7280', 0.15),
              color: getAreaConfig(selectedType.area)?.color || 'text.secondary',
              fontWeight: 500,
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default DocumentTypeSelector;
